<template>
    <v-col cols="12">
        <v-row no-gutters>
            <v-col cols="6">
                <v-row no-gutters>
                    <v-col class="title">
                        {{ $t("Preview") }}
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="body-2 g5--text">
                        {{ $t("Import started") }}:
                        {{ formatDateTime(importCreatedAt) }}
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="6">
                <v-row
                    no-gutters
                    align="center"
                    justify="end"
                >
                    <help-button :link="helpLinkKey" />
                    <v-col class="shrink">
                        <secondary-button @click="backAction">
                            {{ $t("Back") }}
                        </secondary-button>
                    </v-col>
                    <v-col class="shrink">
                        <primary-button
                            :disabled="richDataTableController && selectedRows.length < 1"
                            @click="onConfirm"
                        >
                            {{ $t('Confirm') }}
                        </primary-button>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="py-5">
            <v-col cols="12" class="d-flex">
                <switch-button
                    :value="onlyNewClients"
                    :label="$t('Only new clients')"
                    @change="setOnlyNewClients($event)"
                />
                <div v-if="ImportResultModule.showCompanyRegister(importType)" class="ml-8">
                    <switch-button
                        :value="updateDataFromCompanyRegister"
                        @change="updateDataFromCompanyRegister = $event"
                    >
                        <template v-slot:label="">
                            {{ $t('Update data from company register') }}
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon class="ml-2" v-on="on">
                                        mdi-information-outline
                                    </v-icon>
                                </template>
                                {{ $t('If switched on, the company data is updated using the local business register') }}
                            </v-tooltip>
                        </template>
                    </switch-button>
                </div>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col cols="12">
                <RichDataTable
                    v-if="richDataTableController"
                    selectable
                    :rich-data-table-controller="richDataTableController"
                    :force-loading="loader.isLoading"
                    :minimalisticFooter="true"
                    :forceShowFooter="true"
                >
                    <template v-slot:item.selected="{ item }">
                        <div class="body-2">
                            {{ ImportResultModule.getMatchedClientAction(selectedRows, item.import_client_id) }}
                        </div>
                    </template>
                    <template v-slot:item.reg_code="{ item }">
                        <v-row
                            no-gutters
                            align="center"
                        >
                            <v-col v-if='item.address_country_code && companyRegister[item.address_country_code.toLowerCase()]' class="shrink mr-5">
                                <v-icon v-if="item.uku_registry_name" color="tretiary">check</v-icon>
                                <div v-else-if="item.reg_code && item.address_country_code.toLowerCase() === 'gb'"></div>
                                <v-tooltip v-else top>
                                    <template v-slot:activator="{ on: show }">
                                        <v-icon
                                            color="error"
                                            v-on="show"
                                        >
                                            mdi-close-circle-outline
                                        </v-icon>
                                    </template>
                                    <span v-if="item.address_country_code.toLowerCase() === 'ee'">{{ $t("We did not find a match in Äriregister") }}</span>
                                    <span v-else-if="item.address_country_code.toLowerCase() === 'gb'">{{ $t("We did not find a match in Companies House") }}</span>
                                </v-tooltip>
                            </v-col>
                            <v-col>
                                {{ item.reg_code }}
                            </v-col>
                        </v-row>
                    </template>
                    <template v-slot:item.is_api="{ item }">
                        <v-row
                            no-gutters
                            align="center"
                        >
                            <v-col class="shrink">
                                <v-icon
                                    v-if="item.is_api"
                                    color="tretiary"
                                >
                                    key
                                </v-icon>
                            </v-col>
                        </v-row>
                    </template>
                    <template v-slot:item.uku_client_id="{ item }">
                        <ImportResultRowClientStatus
                            :item="item"
                            :selected-import-clients="selectedRows"
                        />
                    </template>
                    <template v-slot:item.address_country_code="{ item }">
                        {{ item.address_country_code ? item.address_country_code.toUpperCase() : '' }}
                    </template>
                    <template v-slot:item.company_register="{ item }">
                        <div v-if="item.address_country_code && companyRegister[item.address_country_code.toLowerCase()]">
                            {{ getCompanyRegisterNameByCountry(item.address_country_code.toLowerCase()) }}
                        </div>
                        <div v-else class="g4--text">
                            {{ $t('No integration yet') }}
                        </div>
                    </template>
                </RichDataTable>
            </v-col>
        </v-row>
        <ImportDoneModal
            v-if="lastExcelImportData"
            :override-company-id="companyId"
            :override-last-excel-import-data="lastExcelImportData"
        />
    </v-col>
</template>

<script>
import { Router } from "@/composables/Router";
import { formatDateTime } from "@/plugins/formatting";
import {computed, onMounted, onUnmounted, reactive, ref, watch} from "@vue/composition-api";
import { $t } from "@/i18n";
import Dialog from "@/components/Dialog.vue";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import RichDataTable from "@/components/RichDataTable/RichDataTable";
import ImportResultModule from "@/views/Import/Result/ImportResultModule";
import ImportResultRowClientStatus from "@/views/Import/Result/ImportResultRowClientStatus";
import ApiIntegrationImport from "@/api/integration/import";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import companyRegister from "@/config/companyRegister.json"
import AppModule from "@/store/modules/app";
import { HELP_LINKS } from "@/helpers/constants";
import {capitalize} from "@/plugins/formatting";
import {uku_send_event, UKU_ACTIVITIES} from "@/helpers/uku_activity";
import RichDataTableController from "@/components/RichDataTable/RichDataTableController";
import Loader from "@/utility/loader";
import ImportDoneModal from "@/components/Dialog/ImportDoneModal/Index.vue";

export default {
    name: "ImportResultView",
    components: {
        ImportDoneModal,
        RichDataTable,
        Dialog,
        ImportResultRowClientStatus
    },
    setup(props, ctx) {
        const { setPageTitle } = AppModule();
        const router = Router();
        const updateDataFromCompanyRegister = ref(true);
        const richDataTableController = ref(null)
        const apiCountry = ref(null)
        const companyId = ref(null)
        const importType = ref(null)
        const onlyNewClients = ref(false)
        const importSessionId = ref(parseInt(router.currentRoute.params.id))
        const loader = reactive(new Loader())
        const lastExcelImportData = ref(null)

        watch(() => ctx.root._route.params.id, async () => {
            importSessionId.value = parseInt(ctx.root._route.params.id)
            await loadData()
        })

        const headers = [
            {
                text: $t("Action"),
                value: "selected",
                sortable: false,
                width: 0,
                default: true
            },
            {
                text: $t("Uku match"),
                value: "uku_client_id",
                sortable: false,
                width: 200,
                default: true
            },
            {
                text: $t("Client"),
                value: "name",
                sortable: false,
                default: true
            },
            {
                text: $t("Business register nr."),
                value: "reg_code",
                sortable: false,
                width: 250,
                align: 'left',
                default: true
            },
            {
                text: $t("Api key"),
                value: "is_api",
                sortable: false,
                width: 100,
                default: true
            },
            {
                text: $t("Country"),
                value: "address_country_code",
                sortable: false,
                width: 100,
                default: true
            },
            {
                text: $t("Company register"),
                value: "company_register",
                sortable: false,
                width: 200,
                default: true
            },
        ];

        const rows = ref([])
        const importCreatedAt = ref(null)

        const backAction = function() {
            router.go(-1);
        };

        onMounted(async () => {
            await loadData()

            const pageTitleFileName = importType.value === 'xlsx' ? 'Excel' : importType.value
            const pageTitle = $t('Import clients from {0}').format(capitalize(pageTitleFileName))
            setPageTitle(pageTitle)
        })

        const setOnlyNewClients = (value) => {
            onlyNewClients.value = value
            let selectedRows
            if (onlyNewClients.value) {
                selectedRows = rows.value.filter(x => !x.uku_client_id)
            } else {
                selectedRows = rows.value
            }

            richDataTableController.value.setSelectedRows(selectedRows)
        }

        const helpLinkKey = computed(() => {
            if (importType.value === 'xlsx') {
                return HELP_LINKS.IMPORTER_STEP3_XLSX
            } else if (importType.value === 'economic') {
                return HELP_LINKS.IMPORTER_STEP3_ECONOMIC
            } else if (importType.value === 'merit_aktiva') {
                return HELP_LINKS.IMPORTER_STEP3_MERIT_AKTIVA
            } else {
                return HELP_LINKS.IMPORTER_STEP3
            }
        })

        const selectedRows = computed(() => {
            if (!richDataTableController.value) {
                return null
            }

            return richDataTableController.value.getSelectedRows()
        })

        const getIncludedList = () => {
            return [...new Set(
                selectedRows.value
                    .map(x => x.import_client_id)
            )];
        };

        const onConfirm = async () => {
            try {
                await ApiIntegrationImport.confirmImport(
                    companyId.value,
                    importSessionId.value,
                    getIncludedList(),
                    updateDataFromCompanyRegister.value
                )
                router.push({ name: 'import_status' })
                // UKU-2600 :: todo metrics - read window
                uku_send_event({event: UKU_ACTIVITIES.ADDED_CLIENT, info: "import"})
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const getCompanyRegisterNameByCountry = (countryCode) => {
            if (countryCode === "ee") {
                return 'Äriregister'
            } else if (countryCode === "gb") {
                return 'Companies house'
            }
            return null
        }

        const getVisibleHeaders = () => {
            let visibleHeaders = []
            visibleHeaders.push('selected')
            visibleHeaders.push('uku_client_id')
            visibleHeaders.push('name')
            visibleHeaders.push('reg_code')
            if (['merit_aktiva', 'smart_accounts'].includes(importType.value)) {
                visibleHeaders.push('is_api')
            }
            visibleHeaders.push('company_register')
            visibleHeaders.push('address_country_code')
            return visibleHeaders
        }

        const getDefaultSelectedRows = () => {
            return rows.value.filter(x => !x.is_ignored)
        }

        const setImportType = (value) => {
            importType.value = value

            let visibleHeaders = getVisibleHeaders()
            richDataTableController.value.setVisibleHeaders(visibleHeaders)
        }


        const loadData = async() => {
            await loader.withLoader(async () => {
                try {
                    const response = await ApiIntegrationImport.getImportResult(importSessionId.value)

                    rows.value = response.output
                    setImportType(response.type)
                    apiCountry.value = response.api_country
                    importCreatedAt.value = response.created_at
                    companyId.value = response.account_id

                    await richDataTableController.value.loadDataFunc()
                    richDataTableController.value.setSelectedRows(getDefaultSelectedRows())

                    if (rows.value.length === 0) {
                        lastExcelImportData.value = response.row_info
                    }
                } catch (ex) {
                    errorDialog.showError(ex)
                }
            })
        }

        onMounted(() => {
            const options = [
                { key: "sortBy", default_value: "name" },
                { key: "sortDesc", default_value: false },
                { key: "itemsPerPage", default_value: -1 },
                { key: "page", default_value: 1 }
            ]

            richDataTableController.value = RichDataTableController.init(headers, options, null, [], 'import_client_id')

            const backendQuery = async () => {
                richDataTableController.value.setRows(rows.value, rows.value.length)
            };

            richDataTableController.value.setLoadDataFunc(backendQuery)
        })

        return {
            backAction,
            formatDateTime,
            ImportResultModule,
            onConfirm,
            updateDataFromCompanyRegister,
            getCompanyRegisterNameByCountry,
            companyRegister,
            helpLinkKey,
            loadData,
            importCreatedAt,
            headers,
            apiCountry,
            richDataTableController,
            onlyNewClients,
            setOnlyNewClients,
            importType,
            selectedRows,
            loader,
            lastExcelImportData,
            companyId
        };
    }
};
</script>
